import React from 'react';

import { type Control, Controller, useFieldArray } from 'react-hook-form';

import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';

import type { FormValues } from './types';

function currentYearMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

interface MonthsFieldArrayProps {
  control: Control<FormValues>;
  isSubmitting: boolean;
}

export default function MonthsFieldArray({ control, isSubmitting }: MonthsFieldArrayProps) {
  const { fields, append, remove } = useFieldArray({ control, name: 'months' });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" fontWeight="medium">
          Months
        </Typography>
        <Button
          variant="text"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => append({ value: currentYearMonth() })}
          disabled={isSubmitting}
        >
          Add Month
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {fields.map((field, index) => (
          <Stack key={field.id} direction="row" alignItems="center" spacing={0.5}>
            <Controller
              name={`months.${index}.value`}
              control={control}
              rules={{ required: 'Required' }}
              render={({ field: f, fieldState }) => (
                <TextField
                  {...f}
                  type="month"
                  size="small"
                  disabled={isSubmitting}
                  error={!!fieldState.error}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 160 }}
                />
              )}
            />
            <Tooltip title="Remove">
              <IconButton size="small" onClick={() => remove(index)} disabled={isSubmitting || fields.length === 1}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
